<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useDialogListener } from '@/composables/useEventBasedDialog';
import checkoutService from '@/services/checkoutService';
import { useWarehouseInfo } from '@/composables/useWarehouseInfo';
import useNotify from '@/composables/useNotify';
import { CheckoutFlowStep, useCheckoutFlow } from '@/composables/useCheckoutFlow';
import { isValidEmail, isValidPhone } from '@/lib/utils';
import { useCountdown } from '@/composables/useCountdown';

const {
  isDialogOpen,
  dialogProps,
  closeDialog,
  resetDialogProps,
  subscribeToDialogEvent,
  unsubscribeFromDialogEvent
} = useDialogListener('open-complete-checkout-dialog');

const { warehouseData } = useWarehouseInfo();
const checkoutFlow = useCheckoutFlow();
const { notify } = useNotify();
const { t } = useI18n();
const checkoutApi = checkoutService();

const showSendBolSection = ref(false);
const isSendEmailEnabled = ref(false);
const isSendSmsEnabled = ref(false);
const hasPrintedBol = ref(false);
const isLoading = ref(false);
const email = ref('');
const phoneNumber = ref('');
const isDocumentLoading = ref(false);
const bolDocumentString = ref<string>('');

const isOpen = computed(() => isDialogOpen.value);

// Required unless the warehouse explicitly turned it off. When it is off, the driver can finish
// right away; print, SMS and email are all offered up front as optional ways to keep a copy.
const isPrintingRequired = computed(
  () => warehouseData.value?.settings?.checkoutBolPrintingRequired !== false
);
const isSendBolSectionVisible = computed(
  () => !isPrintingRequired.value || showSendBolSection.value
);
const isCompleteCheckoutEnabled = computed(() => !isPrintingRequired.value || hasPrintedBol.value);

const fetchBolDocument = async (): Promise<void> => {
  isDocumentLoading.value = true;
  try {
    const bolData = await checkoutApi.getBol(
      warehouseData.value.id,
      dialogProps.value.appointmentId
    );
    if (!bolData.signedDoc) {
      throw new Error('Signed BOL is not available');
    }
    const bolPdf = await checkoutApi.previewBolPdf(warehouseData.value.id, bolData.signedDoc.id);
    const blob = new Blob([bolPdf], { type: 'application/pdf' });
    bolDocumentString.value = `${URL.createObjectURL(blob)}#toolbar=0&navpanes=0&scrollbar=0`;
  } finally {
    isDocumentLoading.value = false;
  }
};

const handlePrintBol = async () => {
  try {
    // If the signed BOL can't be loaded we must not enable checkout or start the timer,
    // so let fetchBolDocument throw and stop here.
    await fetchBolDocument();

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    iframe.onload = function onload() {
      setTimeout(() => {
        iframe.focus();
        iframe.contentWindow.print();
      }, 1);
    };

    iframe.src = bolDocumentString.value;
    hasPrintedBol.value = true;

    initializeTimer();
  } catch (error) {
    notify(t('completeCheckoutDialog.errors.fetchBolFailed'), 'error');
    console.error('Error preparing BOL for printing:', error);
  }
};

const handleFinishCheckout = async () => {
  isLoading.value = true;
  try {
    await checkoutApi.completeCheckout(warehouseData.value.id, {
      assetVisitId: dialogProps.value.assetVisitId,
      appointmentId: dialogProps.value.appointmentId,
      ...(dialogProps.value.customFormsData?.length && {
        customFormsData: dialogProps.value.customFormsData
      })
    });

    closeDialog();
    checkoutFlow.setNextStep(CheckoutFlowStep.CheckoutCompletePage);
  } catch (error) {
    notify(t('common.errors.finishCheckoutFailed'), 'error');
    console.error('Error finishing checkout:', error);
  } finally {
    isLoading.value = false;
  }
};

const handleSendBolEmail = async () => {
  isLoading.value = true;
  try {
    await checkoutApi.sendBolEmailSms({
      warehouseId: warehouseData.value.id,
      appointmentId: checkoutFlow.appointmentId.value,
      contactData: email.value,
      type: 'email'
    });

    notify(t('completeCheckoutDialog.success.emailSent'), 'success');
  } catch (error) {
    notify(t('completeCheckoutDialog.errors.sendEmailFailed'), 'error');
    console.error('Error sending BOL to email:', error);
  } finally {
    isLoading.value = false;
  }
};

const handleSendBolSms = async () => {
  isLoading.value = true;
  try {
    await checkoutApi.sendBolEmailSms({
      warehouseId: warehouseData.value.id,
      appointmentId: checkoutFlow.appointmentId.value,
      contactData: phoneNumber.value,
      type: 'sms'
    });

    notify(t('completeCheckoutDialog.success.smsSent'), 'success');
  } catch (error) {
    notify(t('completeCheckoutDialog.errors.sendSmsFailed'), 'error');
    console.error('Error sending BOL to phone number:', error);
  } finally {
    isLoading.value = false;
  }
};

const revokeObjectUrl = () => {
  if (bolDocumentString.value && bolDocumentString.value.startsWith('blob:')) {
    URL.revokeObjectURL(bolDocumentString.value.split('#')[0]);
  }
};

const { timer, initializeTimer, stopTimer, secondsRemaining } = useCountdown(
  30,
  handleFinishCheckout
);

const onAfterLeave = () => {
  resetDialogProps();
  revokeObjectUrl();
};

watch(email, newVal => {
  isSendEmailEnabled.value = isValidEmail(newVal);
});

watch(phoneNumber, newVal => {
  isSendSmsEnabled.value = isValidPhone(newVal);
});

onMounted(async () => {
  subscribeToDialogEvent();
});

onUnmounted(() => {
  unsubscribeFromDialogEvent();
  revokeObjectUrl();
  stopTimer();
});
</script>

<template>
  <v-dialog v-model="isOpen" persistent max-width="625" @after-leave="onAfterLeave">
    <v-card :rounded="false" class="juno-i18n-exclude">
      <div class="d-flex flex-row align-center p-4 complete-checkout-bol-signed">
        <v-icon size="large" color="success" class="mr-2">mdi-check-circle-outline</v-icon>
        <p class="typography-heading-md">{{ t('completeCheckoutDialog.title') }}</p>
      </div>

      <div class="d-flex flex-column gap-8 p-5 mt-5">
        <p class="typography-heading-xlg-bold">
          {{
            isPrintingRequired
              ? t('completeCheckoutDialog.printInstructions')
              : t('completeCheckoutDialog.deliveryOptionsInstructions')
          }}
        </p>

        <v-btn
          prepend-icon="mdi-printer"
          color="primary"
          data-testid="print-bol"
          @click="handlePrintBol"
          :disabled="isDocumentLoading"
          :loading="isDocumentLoading">
          {{ t('completeCheckoutDialog.printButton') }}
        </v-btn>

        <p class="d-flex justify-center">
          {{ t('completeCheckoutDialog.printHelp') }}
        </p>
      </div>

      <div class="d-flex flex-column gap-5 p-5">
        <v-divider></v-divider>

        <v-btn
          v-if="isPrintingRequired"
          variant="text"
          size="small"
          class="complete-checkout-btn"
          data-testid="toggle-send-bol-section"
          @click="
            () => {
              stopTimer();
              showSendBolSection = !showSendBolSection;
            }
          ">
          {{ t('completeCheckoutDialog.toggleSendSection') }}
        </v-btn>

        <div
          v-if="isSendBolSectionVisible"
          class="d-flex flex-column p-5 complete-checkout-send-bol-section"
          data-testid="send-bol-section">
          <v-row class="align-center">
            <v-col cols="9">
              <v-label class="typography-body-md-bold mb-2">
                {{ t('completeCheckoutDialog.sendSmsLabel') }}
              </v-label>
              <v-text-field
                variant="outlined"
                density="default"
                :placeholder="t('completeCheckoutDialog.smsPlaceholder')"
                bg-color="white"
                type="tel"
                v-model="phoneNumber"></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-btn
                prepend-icon="mdi-message-outline"
                color="primary"
                size="large"
                class="mt-1"
                data-testid="send-bol-sms"
                :disabled="!isSendSmsEnabled || isLoading"
                :loading="isLoading"
                @click="handleSendBolSms">
                {{ t('common.actions.send') }}
              </v-btn>
            </v-col>
          </v-row>
          <v-row class="align-center">
            <v-col cols="9">
              <v-label class="typography-body-md-bold mb-2">
                {{ t('completeCheckoutDialog.sendEmailLabel') }}
              </v-label>
              <v-text-field
                variant="outlined"
                density="default"
                bg-color="white"
                :placeholder="t('completeCheckoutDialog.emailPlaceholder')"
                v-model="email"></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-btn
                prepend-icon="mdi-email-outline"
                color="primary"
                size="large"
                class="mt-1"
                data-testid="send-bol-email"
                :disabled="!isSendEmailEnabled || isLoading"
                :loading="isLoading"
                @click="handleSendBolEmail">
                {{ t('common.actions.send') }}
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <v-btn
          color="primary"
          data-testid="finish-checkout"
          :disabled="!isCompleteCheckoutEnabled"
          @click="handleFinishCheckout">
          {{ t('completeCheckoutDialog.finishCheckoutButton') }}
          <span v-if="timer">({{ secondsRemaining }})</span>
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.complete-checkout-bol-signed {
  background: $color-success-20;
}
.complete-checkout-btn {
  color: $color-text-link;
}
.complete-checkout-send-bol-section {
  background-color: $color-background-secondary;
}
</style>
